import { useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import 'leaflet/dist/leaflet.css';

const STATUS_STYLE = {
  add: { color: '#2e7d32', label: 'Added vs live targeting' },
  keep: { color: '#90a4ae', label: 'Unchanged' },
  remove: { color: '#d32f2f', label: 'Removed' },
  selected: { color: '#1e88e5', label: 'In selection' },
};

function LegendDot({ color }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: color,
        border: '1.5px solid #fff',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
        marginRight: 0.75,
        verticalAlign: 'middle',
      }}
    />
  );
}

/**
 * ZIP centroid map of the current selection. When a live-targeting diff is
 * available (exactly one ad set selected), dots are colored added / unchanged
 * and removed ZIPs are plotted too; otherwise all dots read "in selection".
 */
export default function SelectionCoverageMap({
  items = [],
  loading = false,
  truncated = false,
  hasDiff = false,
  height = 320,
}) {
  const bounds = useMemo(() => {
    if (!items.length) return null;
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;
    items.forEach((item) => {
      if (item.latitude < minLat) minLat = item.latitude;
      if (item.latitude > maxLat) maxLat = item.latitude;
      if (item.longitude < minLng) minLng = item.longitude;
      if (item.longitude > maxLng) maxLng = item.longitude;
    });
    const pad = 0.05;
    return [
      [minLat - pad, minLng - pad],
      [maxLat + pad, maxLng + pad],
    ];
  }, [items]);

  const counts = useMemo(() => {
    const result = { add: 0, keep: 0, remove: 0, selected: 0 };
    items.forEach((item) => {
      const status = STATUS_STYLE[item.status] ? item.status : 'selected';
      result[status] += 1;
    });
    return result;
  }, [items]);

  if (loading) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!items.length || !bounds) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          No ZIPs in the current selection to map.
        </Typography>
      </Box>
    );
  }

  const legendEntries = hasDiff
    ? ['add', 'keep', 'remove']
    : ['selected'];

  return (
    <Box>
      <Box
        sx={{
          height,
          borderRadius: 1,
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <MapContainer
          bounds={bounds}
          boundsOptions={{ padding: [12, 12] }}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
          attributionControl={false}
          preferCanvas
          maxZoom={13}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            subdomains={['a', 'b', 'c', 'd']}
            maxZoom={13}
            keepBuffer={1}
            updateWhenIdle
            updateWhenZooming={false}
          />
          {items.map((item) => {
            const style = STATUS_STYLE[item.status] || STATUS_STYLE.selected;
            return (
              <CircleMarker
                key={`${item.status || 'selected'}-${item.zip}`}
                center={[item.latitude, item.longitude]}
                radius={4.5}
                pathOptions={{
                  color: style.color,
                  fillColor: style.color,
                  fillOpacity: 0.7,
                  weight: 1,
                }}
              >
                <Tooltip direction="top" offset={[0, -4]}>
                  <span>
                    {item.zip}
                    {item.territory_name ? ` · ${item.territory_name}` : ''}
                    {item.status === 'add' ? ' · added' : ''}
                    {item.status === 'remove' ? ' · removed' : ''}
                  </span>
                </Tooltip>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </Box>
      <Stack
        direction="row"
        spacing={2}
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 0.75 }}
      >
        {legendEntries.map((key) => (
          <Typography key={key} variant="caption" color="text.secondary">
            <LegendDot color={STATUS_STYLE[key].color} />
            {STATUS_STYLE[key].label} ({counts[key].toLocaleString()})
          </Typography>
        ))}
        {truncated && (
          <Typography variant="caption" color="warning.main">
            Large selection — only the first 6,000 ZIPs are plotted.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
